import type { ProposalCompanyResponse } from 'services/actions/ai_management_proposal_company';
import type { ProposalDeclarationResponse } from 'services/actions/ai_management_proposal_documents';
import {
  ProposalDocumentBidding,
  ProposalDocumentField,
  ProposalDocumentSignature,
  VAZIO,
  buildSignature,
} from './structure';

export type DeclarationAlign = 'left' | 'center' | 'right' | 'justify';

export interface DeclarationRun {
  texto: string;
  negrito: boolean;
  italico: boolean;
  sublinhado: boolean;
}

export interface DeclarationParagraph {
  tipo: 'paragrafo';
  alinhamento: DeclarationAlign;
  trechos: DeclarationRun[];
}

export interface DeclarationList {
  tipo: 'lista';
  ordenada: boolean;
  itens: DeclarationParagraph[];
}

export type DeclarationBlock = DeclarationParagraph | DeclarationList;

export interface DeclarationStructure {
  titulo: string;
  referencia: ProposalDocumentField[];
  blocos: DeclarationBlock[];
  assinatura: ProposalDocumentSignature;
}

export interface DeclarationFile {
  estrutura: DeclarationStructure;
  // Sem extensão; o gerador acrescenta .pdf / .docx.
  nomeBase: string;
}

const ALINHAMENTOS: DeclarationAlign[] = ['left', 'center', 'right', 'justify'];
const MARCAS = {
  STRONG: 'negrito',
  B: 'negrito',
  EM: 'italico',
  I: 'italico',
  U: 'sublinhado',
} as const;
const CAMPO_VAZIO = /^\[Inserir .*\]$/;

type Marcas = Pick<DeclarationRun, 'negrito' | 'italico' | 'sublinhado'>;

const alinhamentoDe = (el: Element): DeclarationAlign => {
  const valor = (el as HTMLElement).style?.textAlign as DeclarationAlign;
  return ALINHAMENTOS.includes(valor) ? valor : 'left';
};

// Junta trechos vizinhos com as mesmas marcas.
const empurrar = (trechos: DeclarationRun[], texto: string, marcas: Marcas) => {
  if (!texto) return;
  const ultimo = trechos[trechos.length - 1];
  if (
    ultimo &&
    ultimo.negrito === marcas.negrito &&
    ultimo.italico === marcas.italico &&
    ultimo.sublinhado === marcas.sublinhado
  ) {
    ultimo.texto += texto;
  } else {
    trechos.push({ texto, ...marcas });
  }
};

// Só o inline aceito pelo sanitizador do back: texto, br, strong/b, em/i, u e span[data-campo]
// (vira texto; "[Inserir …]" sai como lacuna). O resto é ignorado, mas o texto dele é mantido.
const lerInline = (node: Node, marcas: Marcas, trechos: DeclarationRun[]) => {
  if (node.nodeType === Node.TEXT_NODE) {
    empurrar(trechos, node.textContent ?? '', marcas);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (el.tagName === 'BR') {
    empurrar(trechos, '\n', marcas);
    return;
  }
  if (el.tagName === 'SPAN' && el.hasAttribute('data-campo')) {
    const valor = (el.textContent ?? '').trim();
    empurrar(trechos, CAMPO_VAZIO.test(valor) || !valor ? VAZIO : valor, marcas);
    return;
  }
  const marca = MARCAS[el.tagName as keyof typeof MARCAS];
  const proximas = marca ? { ...marcas, [marca]: true } : marcas;
  el.childNodes.forEach((filho) => lerInline(filho, proximas, trechos));
};

const SEM_MARCAS: Marcas = { negrito: false, italico: false, sublinhado: false };

const paragrafoDe = (el: Element, alinhamento?: DeclarationAlign): DeclarationParagraph => {
  const trechos: DeclarationRun[] = [];
  el.childNodes.forEach((filho) => lerInline(filho, SEM_MARCAS, trechos));
  return { tipo: 'paragrafo', alinhamento: alinhamento ?? alinhamentoDe(el), trechos };
};

// Cada <li> vira um parágrafo; o <p> de dentro (como o editor grava) leva o alinhamento do li.
const itemDe = (li: Element): DeclarationParagraph => {
  const paragrafos = Array.from(li.children).filter((filho) => filho.tagName === 'P');
  if (paragrafos.length === 0) return paragrafoDe(li);
  const alinhamento =
    alinhamentoDe(li) !== 'left' ? alinhamentoDe(li) : alinhamentoDe(paragrafos[0]);
  const trechos: DeclarationRun[] = [];
  paragrafos.forEach((p, index) => {
    if (index > 0) empurrar(trechos, '\n', SEM_MARCAS);
    p.childNodes.forEach((filho) => lerInline(filho, SEM_MARCAS, trechos));
  });
  return { tipo: 'paragrafo', alinhamento, trechos };
};

// Conversor único do HTML restrito do texto_final para os dois geradores (DOCX e PDF).
export const htmlParaBlocos = (html: string): DeclarationBlock[] => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const blocos: DeclarationBlock[] = [];
  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const texto = (node.textContent ?? '').trim();
      if (texto)
        blocos.push({
          tipo: 'paragrafo',
          alinhamento: 'left',
          trechos: [{ texto, ...SEM_MARCAS }],
        });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const itens = Array.from(el.children)
        .filter((filho) => filho.tagName === 'LI')
        .map(itemDe);
      if (itens.length) blocos.push({ tipo: 'lista', ordenada: el.tagName === 'OL', itens });
      return;
    }
    blocos.push(paragrafoDe(el));
  });
  return blocos.filter(
    (bloco) => bloco.tipo === 'lista' || bloco.trechos.some((t) => t.texto.trim()),
  );
};

// Declarações que viram arquivo: de proposta, "Antes do lance", confirmadas e com texto. As que já
// estão no modelo (no_modelo) também saem aqui enquanto o arquivo da proposta não as incluir; as
// assinaladas no portal nunca.
export const declaracoesParaArquivo = (declaracoes: ProposalDeclarationResponse[]) =>
  declaracoes.filter(
    (d) =>
      d.categoria === 'proposta' &&
      d.momento === 'antes_do_lance' &&
      d.status === 'confirmado' &&
      !d.ignorada &&
      !d.assinalar_no_portal &&
      Boolean((d.texto_final ?? '').trim()),
  );

const normalizar = (valor: string) =>
  valor
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const ouVazio = (valor: string | null | undefined) => (valor ?? '').trim() || VAZIO;

export const buildDeclarationFiles = (
  declaracoes: ProposalDeclarationResponse[],
  bidding: ProposalDocumentBidding,
  company: ProposalCompanyResponse | null,
  geradoEm: Date,
): DeclarationFile[] => {
  const referencia: ProposalDocumentField[] = [
    { rotulo: 'Órgão', valor: ouVazio(bidding.public_body?.nome || bidding.orgao_nome) },
    { rotulo: 'Modalidade', valor: ouVazio(bidding.modality?.nome) },
    { rotulo: 'Edital', valor: ouVazio(bidding.edital) },
  ];
  const assinatura = buildSignature(company, geradoEm);
  const edital = normalizar(bidding.edital ?? '') || 'licitacao';
  const usados = new Map<string, number>();

  return declaracoesParaArquivo(declaracoes).map((declaracao) => {
    // O nome já costuma começar por "Declaração …": não repete o prefixo.
    const nome = normalizar(declaracao.nome) || 'declaracao';
    const base = `${nome.startsWith('declaracao') ? nome : `declaracao-${nome}`}-${edital}`;
    const vezes = (usados.get(base) ?? 0) + 1;
    usados.set(base, vezes);
    return {
      nomeBase: vezes > 1 ? `${base}-${vezes}` : base,
      estrutura: {
        titulo: declaracao.nome.toUpperCase(),
        referencia,
        blocos: htmlParaBlocos(declaracao.texto_final ?? ''),
        assinatura,
      },
    };
  });
};
